import { Fragment, useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { logout } from '../../../../actions/auth';
import { backendHOST } from '../../../../client-config/client-config';

import {
  InvoiceIssuerTypes,
  PaymentMethods,
} from '../../../../utils/enums/userTypes';

import { withRouter } from '../../../hooks/withRouter';

const url = `${backendHOST}/api/scriptantiaAdmin`;
const urlEnd = '/Income';

const emptyForm = {
  datum: '',
  osszeg: '',
  descr: '',
  fiz_mod: '',
  vallalkozas: '',
};

const FormIncome = ({ auth }) => {
  const [incomes, setIncomes] = useState([]);

  const [searchText, setSearchText] = useState('');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    'x-auth-token': localStorage.token,
  };

  const formatDateForInput = (value) => {
    if (!value) return '';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString().slice(0, 10);
  };

  const numberOrNull = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    return Number(value);
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${url}${urlEnd}`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error('Nem sikerült betölteni a bevételeket.');
      }

      const data = await response.json();

      setIncomes(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Ismeretlen hiba történt betöltés közben.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPaymentMethodLabel = (paymentMethod) => {
    const item = PaymentMethods.find(
      (method) => String(method.key) === String(paymentMethod),
    );

    return item?.name || paymentMethod || '';
  };

  const getInvoiceIssuerLabel = (issuer) => {
    const item = InvoiceIssuerTypes.find(
      (invoiceIssuer) => String(invoiceIssuer.key) === String(issuer),
    );

    return item?.name || issuer || '';
  };

  const sortedIncomes = useMemo(() => {
    return [...incomes].sort((a, b) => {
      const dateA = a.datum ? new Date(a.datum).getTime() : 0;
      const dateB = b.datum ? new Date(b.datum).getTime() : 0;

      return dateB - dateA;
    });
  }, [incomes]);

  const filteredIncomes = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) {
      return sortedIncomes;
    }

    return sortedIncomes.filter((income) =>
      [
        income.id_bizonylat,
        income.datum,
        income.osszeg,
        income.descr,
        income.fiz_mod,
        getPaymentMethodLabel(income.fiz_mod),
        income.vallalkozas,
        getInvoiceIssuerLabel(income.vallalkozas),
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch),
        ),
    );
  }, [sortedIncomes, searchText]);

  const handleCreateClick = () => {
    setFormData({
      ...emptyForm,
      datum: formatDateForInput(new Date()),
      fiz_mod: PaymentMethods?.[0]?.key || '',
      vallalkozas: InvoiceIssuerTypes?.[0]?.key || '',
    });

    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (saving) return;

    setDialogOpen(false);
    setFormData(emptyForm);
  };

  const handleFormChange = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const normalizePayload = (data) => {
    return {
      datum: data.datum || null,
      osszeg: numberOrNull(data.osszeg),
      descr: data.descr || '',
      fiz_mod: data.fiz_mod || null,
      vallalkozas: data.vallalkozas || null,

      // Ha a backend nem tokenből állítja, hasznos lehet:
      createdBy: auth.user.ext_id_user,
    };
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const payload = normalizePayload(formData);

      const response = await fetch(`${url}${urlEnd}`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Nem sikerült rögzíteni a bevételt.');
      }

      setMessage('Bevétel rögzítve.');

      setDialogOpen(false);
      setFormData(emptyForm);

      await loadData();
    } catch (err) {
      setError(err.message || 'Mentés közben hiba történt.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (income) => {
    const confirmed = window.confirm(
      `Biztosan törlöd ezt a bevételt?\n\n${formatDateForInput(
        income.datum,
      )} - ${income.osszeg} Ft\n${income.descr || ''}`,
    );

    if (!confirmed) return;

    setDeletingId(income.id_bizonylat);
    setError(null);

    try {
      const response = await fetch(`${url}${urlEnd}`, {
        method: 'DELETE',
        headers: authHeaders,
        body: JSON.stringify(income.id_bizonylat),
      });

      if (!response.ok) {
        throw new Error('Nem sikerült törölni a bevételt.');
      }

      setMessage('Bevétel törölve.');

      await loadData();
    } catch (err) {
      setError(err.message || 'Törlés közben hiba történt.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Fragment>
      <Box sx={{ p: 2 }}>
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent='space-between'
                spacing={2}
              >
                <Box>
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    Bevételek rögzítése
                  </Typography>

                  <Typography variant='body2' color='text.secondary'>
                    Egyéb bevételek rögzítése és kezelése.
                  </Typography>
                </Box>

                <Button
                  variant='contained'
                  startIcon={<AddIcon />}
                  onClick={handleCreateClick}
                >
                  Új bevétel
                </Button>
              </Stack>

              <TextField
                label='Keresés'
                size='small'
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                sx={{ maxWidth: 320 }}
              />

              {error && (
                <Alert severity='error' onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant='outlined'>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Dátum</TableCell>
                        <TableCell align='right'>Összeg</TableCell>
                        <TableCell>Megjegyzés</TableCell>
                        <TableCell>Fizetési mód</TableCell>
                        <TableCell>Vállalkozás</TableCell>
                        <TableCell align='right'>Művelet</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredIncomes.map((income) => {
                        const rowDeleting = deletingId === income.id_bizonylat;

                        return (
                          <TableRow key={income.id_bizonylat} hover>
                            <TableCell>
                              {formatDateForInput(income.datum) || '-'}
                            </TableCell>

                            <TableCell align='right'>{income.osszeg}</TableCell>

                            <TableCell>{income.descr}</TableCell>

                            <TableCell>
                              {getPaymentMethodLabel(income.fiz_mod)}
                            </TableCell>

                            <TableCell>
                              {getInvoiceIssuerLabel(income.vallalkozas)}
                            </TableCell>

                            <TableCell align='right'>
                              <Tooltip title='Törlés'>
                                <span>
                                  <IconButton
                                    size='small'
                                    color='error'
                                    disabled={rowDeleting}
                                    onClick={() => handleDelete(income)}
                                  >
                                    <DeleteIcon fontSize='small' />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {filteredIncomes.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align='center'>
                            Nincs megjeleníthető bevétel.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Új bevétel</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label='Dátum'
              type='date'
              value={formData.datum}
              onChange={(event) =>
                handleFormChange('datum', event.target.value)
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label='Összeg'
              type='number'
              value={formData.osszeg}
              onChange={(event) =>
                handleFormChange('osszeg', event.target.value)
              }
              fullWidth
            />

            <TextField
              label='Megjegyzés'
              value={formData.descr}
              onChange={(event) =>
                handleFormChange('descr', event.target.value)
              }
              multiline
              minRows={3}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel id='income-payment-method-label'>
                Fizetési mód
              </InputLabel>
              <Select
                labelId='income-payment-method-label'
                label='Fizetési mód'
                value={formData.fiz_mod}
                onChange={(event) =>
                  handleFormChange('fiz_mod', event.target.value)
                }
              >
                {PaymentMethods.map((method) => (
                  <MenuItem key={method.key} value={String(method.key)}>
                    {method.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id='income-vallalkozas-label'>Vállalkozás</InputLabel>
              <Select
                labelId='income-vallalkozas-label'
                label='Vállalkozás'
                value={formData.vallalkozas}
                onChange={(event) =>
                  handleFormChange('vallalkozas', event.target.value)
                }
              >
                {InvoiceIssuerTypes.map((issuer) => (
                  <MenuItem key={issuer.key} value={String(issuer.key)}>
                    {issuer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} disabled={saving}>
            Mégsem
          </Button>

          <Button variant='contained' onClick={handleSave} disabled={saving}>
            {saving ? 'Mentés...' : 'Mentés'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        message={message}
      />
    </Fragment>
  );
};

FormIncome.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { logout })(withRouter(FormIncome));
